import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { CartItem, CartService } from './cart.service';
import { loadStripe } from '@stripe/stripe-js';
import { ToastrService } from 'ngx-toastr';
import { LanguageService } from './language.service';
import { tap, retry, catchError, of, map } from 'rxjs';
import {
    Variant,
    DeliveryDate,
} from '../components/hof/product/product.component';
import { ApiService, SingleProductResponse } from './api.service';
import { DOMHandlerService } from './domhandler.service';
import { TranslateService } from '@ngx-translate/core';
import { ProductService } from './product.service';
import { ShopService } from './shop.service';

@Injectable({
    providedIn: 'root',
})
export class CheckoutService {
    cartService = inject(CartService);
    productService = inject(ProductService);
    apiService = inject(ApiService);
    domHandler = inject(DOMHandlerService);
    toastService = inject(ToastrService);
    languageService = inject(LanguageService);
    translationService = inject(TranslateService);
    shopService = inject(ShopService);

    async setSessionId() {
        if (!this.shopService.config.value()?.data.ordersPossible) {
            this.translationService
                .get('shop.ordersSoonAvailable')
                .subscribe((translation) => {
                    this.toastService.error(translation);
                });
            return;
        }
        const response = await fetch(
            `${environment.apiUrl}/api/checkout/create-session`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: this.getJSONBody(this.cartService.cartItems()),
            },
        );
        const { id } = await response.json();
        return id;
    }

    async setSessionIdForOneProduct(cartItem: CartItem) {
        if (!this.shopService.config.value()?.data.ordersPossible) {
            this.translationService
                .get('shop.ordersSoonAvailable')
                .subscribe((translation) => {
                    this.toastService.error(translation);
                });
            return;
        }
        const response = await fetch(
            `${environment.apiUrl}/api/checkout/create-session`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: this.getJSONBody([cartItem]),
            },
        );
        const { id } = await response.json();
        return id;
    }

    private getJSONBody(products: CartItem[]) {
        const isOnlyVouchers = products.every(
            (item) => item.product.productType === 'voucher',
        );
        return JSON.stringify({
            products: products.map((item) => ({
                documentId: item.product.documentId,
                amount: item.amount,
                selectedVariant: item.selectedVariant.uuid,
                selectedDeliveryDate: item.selectedDeliveryDate?.documentId,
            })),
            cancelUrl: window.location.href,
            languageCode: this.languageService.getLanguageCode,
            isOnlyVouchers,
        });
    }

    async redirectToCheckoutForOneProduct(cartItem: CartItem) {
        const stripe = await loadStripe(environment.stripeKey);
        if (stripe) {
            this.cartService.umami.track(
                'buy-now-button-click',
                this.cartService.umami.getCartItemPayload(cartItem),
            );
            const sessionId = await this.setSessionIdForOneProduct(cartItem);
            const { error } = await stripe.redirectToCheckout({
                sessionId: sessionId!,
            });

            if (error) {
                console.error('Error redirecting to Stripe Checkout:', error);
                this.toastService.error('Error redirecting to Stripe Checkout');
            }
        }
    }

    async redirectToCheckout() {
        const stripe = await loadStripe(environment.stripeKey);
        if (stripe) {
            this.cartService.umami.track('checkout-button-click');
            const sessionId = await this.setSessionId();
            const { error } = await stripe.redirectToCheckout({
                sessionId: sessionId!,
            });

            if (error) {
                console.error('Error redirecting to Stripe Checkout:', error);
                this.toastService.error('Error redirecting to Stripe Checkout');
            }
        }
    }

    revalidateCartItem(item: CartItem, title: string) {
        return this.apiService
            .getProductByDocumentId(item.product.documentId)
            .pipe(
                tap({
                    error: (err) => {
                        console.log('Product not found, retrying', err);
                    },
                }),
                retry({
                    delay: 1000,
                    count: 2,
                }),
                catchError((err) => {
                    this.translationService
                        .get('product.productUnavailable', {
                            productName: item.product.name,
                        })
                        .subscribe((translation) =>
                            this.toastService.error(translation, title, {
                                disableTimeOut: true,
                            }),
                        );
                    this.cartService.deleteFromCart(item);
                    return of(null);
                }),
                map((product) => {
                    if (!product) {
                        return null;
                    }

                    if (!this.selectedVariantIsAvailable(product, item)) {
                        console.log('Variant not available');
                        this.translationService
                            .get('product.variantUnavailable', {
                                productName: item.product.name,
                                variant: this.productService.getVariantLabel(
                                    item.product,
                                    item.selectedVariant,
                                ),
                            })
                            .subscribe((translation) =>
                                this.toastService.error(translation, title, {
                                    disableTimeOut: true,
                                }),
                            );
                        this.cartService.deleteFromCart(item);
                        return null;
                    }

                    if (
                        item.selectedDeliveryDate &&
                        !this.selectedDeliveryDateIsAvailable(product, item)
                    ) {
                        console.log('Delivery date not available');
                        this.translationService
                            .get('product.deliveryDateUnavailable', {
                                selectedDeliveryDate:
                                    this.domHandler.formatDate(
                                        item.selectedDeliveryDate.date,
                                        this.languageService.getLanguageCode,
                                    ),
                                productName: item.product.name,
                            })
                            .subscribe((translation) =>
                                this.toastService.error(translation, title, {
                                    disableTimeOut: true,
                                }),
                            );
                        this.cartService.deleteFromCart(item);
                        return null;
                    }

                    return product;
                }),
            );
    }

    private selectedVariantIsAvailable(
        product: SingleProductResponse,
        item: CartItem,
    ) {
        return product.data.variants.some(
            (variant: Variant) =>
                variant.uuid === item.selectedVariant.uuid &&
                variant.amount >= item.amount &&
                variant.weight === item.selectedVariant.weight,
        );
    }

    private selectedDeliveryDateIsAvailable(
        product: SingleProductResponse,
        item: CartItem,
    ) {
        return product.data.deliveryDates.some(
            (date: DeliveryDate) =>
                date.documentId === item.selectedDeliveryDate?.documentId &&
                date.date === item.selectedDeliveryDate?.date,
        );
    }
}
